import { defineStore } from 'pinia';

type UserPaintApproved = {
  imageUrl: string;
};

const useApprovedUserPaintsStore = defineStore('approvedUserPaints', () => {
  const paints = ref<UserPaintApproved[]>([]);
  const hasLoaded = ref(false);
  const dataRequestPromise = ref<Promise<void> | null>(null);
  const imagePrefetchPromise = ref<Promise<void> | null>(null);
  const imagePrefetchAbortController = ref<AbortController | null>(null);
  const prefetchedImageUrls = ref<Set<string>>(new Set());

  const photoUrls = computed(() => paints.value.map(p => p.imageUrl));

  const fetchApprovedPaints = async ({ force = false }: { force?: boolean } = {}) => {
    if (hasLoaded.value && !force) return;
    if (dataRequestPromise.value && !force) {
      await dataRequestPromise.value;
      return;
    }

    dataRequestPromise.value = (async () => {
      try {
        const { $api } = useNuxtApp();
        paints.value = await $api.userPaints.getApprovedPaints();
      }
      catch (err) {
        console.warn('[approvedUserPaints] Не удалось загрузить рисунки:', err);
        paints.value = [];
      }
      finally {
        hasLoaded.value = true;
      }
    })();

    try {
      await dataRequestPromise.value;
    }
    finally {
      dataRequestPromise.value = null;
    }
  };

  const shouldSkipPrefetchByNetwork = () => {
    if (!import.meta.client) return true;

    const connection = (navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    }).connection;

    if (!connection) return false;
    if (connection.saveData) return true;

    const connectionType = connection.effectiveType;
    return connectionType === 'slow-2g' || connectionType === '2g';
  };

  const prefetchApprovedImages = async ({
    limit = 24,
    concurrency = 2,
  }: {
    limit?: number;
    concurrency?: number;
  } = {}) => {
    if (!import.meta.client) return;
    if (shouldSkipPrefetchByNetwork()) return;
    if (imagePrefetchPromise.value) {
      await imagePrefetchPromise.value;
      return;
    }

    await fetchApprovedPaints();

    const urlsToPrefetch = photoUrls.value
      .filter(url => typeof url === 'string' && url.length > 0)
      .filter(url => !prefetchedImageUrls.value.has(url))
      .slice(0, Math.max(limit, 0));

    if (urlsToPrefetch.length === 0) return;

    const workerCount = Math.max(1, Math.floor(concurrency));
    const controller = new AbortController();
    imagePrefetchAbortController.value = controller;

    const prefetchOne = ({ url, signal }: { url: string; signal: AbortSignal }) => {
      return new Promise<void>((resolve) => {
        if (!import.meta.client) {
          resolve();
          return;
        }
        if (signal.aborted) {
          resolve();
          return;
        }

        const image = new Image();
        let isSettled = false;

        const finish = () => {
          if (isSettled) return;
          isSettled = true;
          resolve();
        };

        const cleanUp = () => {
          image.onload = null;
          image.onerror = null;
          signal.removeEventListener('abort', onAbort);
        };

        const onAbort = () => {
          image.src = '';
          cleanUp();
          finish();
        };

        signal.addEventListener('abort', onAbort, { once: true });

        image.onload = () => {
          prefetchedImageUrls.value.add(url);
          cleanUp();
          finish();
        };

        image.onerror = () => {
          cleanUp();
          finish();
        };

        image.decoding = 'async';
        image.src = url;
      });
    };

    imagePrefetchPromise.value = (async () => {
      let cursor = 0;

      const runWorker = async () => {
        while (cursor < urlsToPrefetch.length) {
          if (controller.signal.aborted) return;
          const index = cursor;
          cursor += 1;
          const url = urlsToPrefetch[index];
          if (!url) continue;
          await prefetchOne({ url, signal: controller.signal });
        }
      };

      const workers = Array.from({ length: workerCount }, () => runWorker());
      await Promise.all(workers);
    })();

    try {
      await imagePrefetchPromise.value;
    }
    finally {
      imagePrefetchPromise.value = null;
      imagePrefetchAbortController.value = null;
    }
  };

  const abortApprovedImagesPrefetch = () => {
    imagePrefetchAbortController.value?.abort();
  };

  return {
    paints,
    photoUrls,
    hasLoaded,
    fetchApprovedPaints,
    prefetchApprovedImages,
    abortApprovedImagesPrefetch,
  };
});

export default useApprovedUserPaintsStore;
